import { Description, Caption } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'

import { useCities } from '@/context/Cities'
import { marginRight } from '@/shared-styles/styles.css'
import { CardDataPriority } from '@/types/property-card'
import { propertyFloorFormatter, propertyFloorSuffix } from '@/utils/floors'
import { getTranslatedStateOfMaintenance } from '@/utils/getTranslatedStateOfMaintenance'
import { joinPaths } from '@/utils/navigation'

import { cardCityAreaDivider } from '../ListingCard/styles.css'
import { PriorityBadge } from '../PriorityCardBadge'

import { CardPriority, PriorityInfo } from '.'

type Props = {
  cardData: CardDataPriority
  handleCardClick: VoidFunction
}

export const CardPriorityWrapper: React.FC<Props> = ({ cardData, handleCardClick, ...props }) => {
  const { lang, t } = useTranslation()
  const cities = useCities()

  const priorityCity = cities.list.find((city) => city.slug === cardData.city)

  const [country] = (cardData.zone?.slug || cardData.city).split('/')
  const locale = lang !== 'en' && country
  const listingLink = joinPaths(locale, 'priority', cardData.slug)

  return (
    <Link passHref href={listingLink} locale={false}>
      <a onClick={handleCardClick} {...props}>
        <CardPriority
          badge={{
            left: <PriorityBadge />,
          }}
          details={[
            ...(cardData.commercialArea
              ? [
                  <>
                    <Description as="span" data-testid="priority-card-area">
                      {cardData.commercialArea}
                    </Description>{' '}
                    <Caption as="span" color="greyscale500">
                      m&sup2;
                    </Caption>
                  </>,
                ]
              : []),

            <>
              <Description as="span" className={marginRight.s} data-testid="priority-card-floor">
                {propertyFloorFormatter(
                  {
                    buildingFloors: cardData.buildingFloors,
                    entireBuilding: false,
                    entranceFloor: cardData.entranceFloor,
                    floorPosition: [],
                  },
                  t
                )}
              </Description>
              <Caption as="span" color="greyscale500">
                {propertyFloorSuffix(
                  {
                    buildingFloors: cardData.buildingFloors,
                    entireBuilding: false,
                    entranceFloor: cardData.entranceFloor,
                    floorPosition: [],
                  },
                  t
                )}
              </Caption>
            </>,
            ,
          ]}
          hasOutdoorSpace={cardData.hasOutdoorSpace}
          headline={
            <>
              {priorityCity?.translatedCityName && (
                <>
                  <span>{priorityCity?.translatedCityName}</span>
                  {cardData.zone && (
                    <>
                      <span className={cardCityAreaDivider} />
                      <span data-testid="listing-card-zone">{cardData.zone.label}</span>
                    </>
                  )}
                </>
              )}
            </>
          }
          image={{
            alt: '',
            src: cardData.photos[0]?.url ?? '',
          }}
          info={
            [
              ...(cardData.rooms
                ? [
                    {
                      heading: t(`common:rooms`),
                      icon: 'rooms',
                      text: cardData.rooms,
                    },
                  ]
                : []),
              ...(cardData.bathrooms
                ? [
                    {
                      heading: t('common:bathrooms'),
                      icon: 'bathroom',
                      text: cardData.bathrooms,
                    },
                  ]
                : []),

              ...(cardData.stateOfMaintenance
                ? [
                    {
                      heading: t('common:status'),
                      text: getTranslatedStateOfMaintenance(cardData.stateOfMaintenance, t),
                    },
                  ]
                : []),
            ] as PriorityInfo[]
          }
          isHighEfficiency={['a', 'aplus', 'b'].includes(cardData.energyRating?.toLowerCase())}
          price={{
            askedPrice: cardData.price,
            estimatedListingPrice: cardData.estimatedListingPrice,
          }}
          status={cardData.status}
          title={cardData.title}
        />
      </a>
    </Link>
  )
}
